import { CommentItem } from "../comments/comment-item/comment-item";
import { IChaseTimelineItemV1 } from "./chase-timeline-item-v1.model";
import { TimelineItem } from "./timeline-item.model";

const NON_BREAKING_SPACE = String.fromCharCode(160);

// Mirrors the backend's HtmlUtils.StripHtml: V1 note text is rich-text HTML from the pend note editor,
// while legacy SQL comments are plain text. Both render through the same innerHTML binding today, so
// this normalizes V1 content down to plain text to match.
export const stripHtml = (html: string): string => {
  if (!html) {
    return "";
  }

  const withoutTags = html.replace(/<[^>]*>/g, "");
  const textarea = document.createElement("textarea");
  textarea.innerHTML = withoutTags;
  return textarea.value.split(NON_BREAKING_SPACE).join(" ").trim();
};

// createdByFirstName sometimes holds the full name alone (no createdByLastName present), and
// sometimes the name is split across both fields - joining the two handles either shape.
const resolveUserName = (item: IChaseTimelineItemV1): string => {
  const fullName = [item.context?.createdByFirstName, item.context?.createdByLastName]
    .filter(name => !!name)
    .join(" ")
    .trim();

  return fullName || "System";
};

const TYPE_TITLE_BY_KEY: { [key: string]: { type: string; title: string } } = {
  COMMENT: { type: "comments", title: "Comment Added" },
  PEND_CREATE: { type: "plus", title: "Pend Created" },
  PEND_UPDATE: { type: "check", title: "Pend Status Changed" },
  DOCUMENT_CREATE: { type: "file", title: "Document Uploaded" },
};

const resolveTypeAndTitle = (item: IChaseTimelineItemV1): { type: string; title: string } => {
  const key = item.eventType === "COMMENT" ? "COMMENT" : `${item.resource}_${item.actionType}`;
  return TYPE_TITLE_BY_KEY[key] || { type: "tasks", title: item.resource || "Activity" };
};

/**
 * Maps V1 chase timeline items into the existing CommentItem shape, so the Comments tab and the
 * "LATEST COMMENT" widget need no template/model changes.
 */
export const toCommentItems = (items: IChaseTimelineItemV1[]): CommentItem[] => {
  if (!items) {
    return [];
  }

  return items
    .filter(item => item.eventType === "COMMENT")
    .map(item => new CommentItem({
      commentText: stripHtml(item.note),
      createdByUserName: resolveUserName(item),
      createdDate: item.createdAt,
    }));
};

/**
 * Maps V1 chase timeline items into the existing TimelineItem shape, so the Timeline tab and the
 * "PEND TIMELINE" widget need no template/model changes.
 */
export const toTimelineItems = (items: IChaseTimelineItemV1[]): TimelineItem[] => {
  if (!items) {
    return [];
  }

  return items.map(item => {
    const { type, title } = resolveTypeAndTitle(item);

    return new TimelineItem({
      type,
      title,
      note: stripHtml(item.note),
      username: resolveUserName(item),
      timestamp: item.createdAt,
    });
  });
};
